import React, { useState } from 'react'
import { Card, Form, Button, Spinner, Alert, Row, Col, Table, Badge } from 'react-bootstrap'
import { listSerialPorts, generateWeighbridgeParser } from '../../generic/crud'
import { PARSER_PRESETS, BAUD_RATES, DATA_BITS_OPTIONS, STOP_BITS_OPTIONS, PARITY_OPTIONS, FLOW_CONTROL_OPTIONS } from '../parserPresets'
import { showToast, TOAST_SUCCESS, TOAST_FAILURE } from '../../../utils/toast/Toast'
import SerialMonitorModal from './SerialMonitorModal'
import AIParserModal from './AIParserModal'

// WEIGHBRIDGE half of a setup group: identification + serial fields + parser presets/editor,
// Detect Ports, Serial Monitor (via getMonitorUrl which saves the group first), and AI parser.
const WeighbridgeConfigSection = ({ config: configProp, onChange, onPresetChange, getMonitorUrl }) => {
    // Guard against a null config (a fresh setup group can arrive without a weighbridge)
    // so the render never crashes reading config.weighbridgeType.
    const config = configProp || {}
    const [scanning, setScanning] = useState(false)
    const [detectedPorts, setDetectedPorts] = useState([])
    const [showSerialModal, setShowSerialModal] = useState(false)
    const [serialUrl, setSerialUrl] = useState(null)
    const [showAIModal, setShowAIModal] = useState(false)
    const [aiGenerating, setAiGenerating] = useState(false)

    const handleScanPorts = async () => {
        try {
            setScanning(true); setDetectedPorts([])
            const ports = (await listSerialPorts()).data || []
            setDetectedPorts(ports)
            if (ports.length === 0) showToast('No serial ports detected', TOAST_FAILURE)
            else showToast(`Found ${ports.length} port${ports.length > 1 ? 's' : ''}`, TOAST_SUCCESS)
        } catch (err) {
            showToast('Failed to scan serial ports', TOAST_FAILURE)
        } finally {
            setScanning(false)
        }
    }

    // Save the group (via parent) then open the live monitor against its resolved SSE url.
    const handleOpenMonitor = async () => {
        const url = await getMonitorUrl()
        if (!url) return
        setSerialUrl(url)
        setShowSerialModal(true)
    }

    // Shared AI generation used by both the live monitor and the manual-paste modal.
    // Returns true on success. Applies the code to the config (preset → Custom).
    const runAIGeneration = async (samples) => {
        if (!samples || samples.length === 0) { showToast('No sample lines to send', TOAST_FAILURE); return false }
        try {
            setAiGenerating(true)
            const response = await generateWeighbridgeParser(samples, config.brandName)
            const code = response.data && response.data.parserFunction
            if (!code) { showToast('AI did not return a parser', TOAST_FAILURE); return false }
            onChange('weighbridgeType', 0)
            onChange('parserFunction', code)
            showToast('AI parser generated and applied', TOAST_SUCCESS)
            return true
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to generate parser with AI'
            showToast(msg, TOAST_FAILURE)
            return false
        } finally {
            setAiGenerating(false)
        }
    }

    const selectedPreset = PARSER_PRESETS.find(p => p.value === config.weighbridgeType)
    let parserError = null
    try { new Function('data', (config.parserFunction || '') + '\nreturn parse(data);') } catch (e) { parserError = e.message }

    return (
        <Card className="mb-3 border">
            <Card.Header className="bg-white py-2 px-3 border-bottom">
                <div className="d-flex align-items-center">
                    <i className="bi bi-usb-drive me-2 text-primary"></i>
                    <span className="fw-semibold small">Weighbridge</span>
                </div>
            </Card.Header>
            <Card.Body className="p-3">
                {/* Brand */}
                <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold text-muted mb-1">Brand Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control size="sm" type="text" value={config.brandName} onChange={(e) => onChange('brandName', e.target.value)}
                        placeholder="e.g., Essae, Orion, Avery Weigh-Tronix" style={{ maxWidth: '400px' }} />
                </Form.Group>

                {/* Serial port + baud */}
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <Form.Label className="small fw-semibold text-muted mb-0">Selected Serial Port <span className="text-danger">*</span></Form.Label>
                                <Button variant="link" size="sm" onClick={handleScanPorts} disabled={scanning} className="p-0 small text-primary">
                                    {scanning ? <><Spinner size="sm" animation="border" className="me-1" />Scanning...</> : <><i className="bi bi-radar me-1"></i>Detect Ports</>}
                                </Button>
                            </div>
                            <Form.Control size="sm" type="text" value={config.serialPort} onChange={(e) => onChange('serialPort', e.target.value)}
                                placeholder="Click 'Detect Ports' or enter manually" />
                            <Form.Text className="text-muted small">e.g., /dev/ttyUSB0 (Linux/Pi), /dev/cu.usbserial-0001 (macOS)</Form.Text>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label className="small fw-semibold text-muted mb-1">Baud Rate <span className="text-danger">*</span></Form.Label>
                            <Form.Select size="sm" value={config.baudRate} onChange={(e) => onChange('baudRate', parseInt(e.target.value))}>
                                {BAUD_RATES.map(rate => <option key={rate} value={rate}>{rate.toLocaleString()}</option>)}
                            </Form.Select>
                            <Form.Text className="text-muted small">Communication speed in bits/sec. Must match the weighbridge setting.</Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                {detectedPorts.length > 0 && (
                    <div className="mb-3 border rounded overflow-hidden">
                        <Table size="sm" className="mb-0" hover>
                            <thead><tr className="bg-light">
                                <th className="ps-3 py-2 small fw-semibold text-muted">Port</th>
                                <th className="py-2 small fw-semibold text-muted">Path</th>
                                <th className="pe-3 py-2 small fw-semibold text-muted text-end">Action</th>
                            </tr></thead>
                            <tbody>
                                {detectedPorts.map(port => {
                                    const isSelected = config.serialPort === port.devicePath
                                    return (
                                        <tr key={port.name} className={isSelected ? 'table-primary' : ''} style={{ cursor: 'pointer' }} onClick={() => onChange('serialPort', port.devicePath)}>
                                            <td className="ps-3 py-2 align-middle"><span className="fw-medium small">{port.name}</span>{port.inUse && <Badge bg="warning" text="dark" className="ms-2">In Use</Badge>}
                                                {port.serialNumber && <div className="text-muted small">SN {port.serialNumber}{!port.serialUnique && ' (not unique)'}</div>}</td>
                                            <td className="py-2 align-middle"><code className="small">{port.devicePath}</code></td>
                                            <td className="pe-3 py-2 align-middle text-end">
                                                {isSelected ? <Badge bg="primary"><i className="bi bi-check-lg me-1"></i>Selected</Badge>
                                                    : <Button variant="outline-primary" size="sm" className="py-0 px-2" onClick={(e) => { e.stopPropagation(); onChange('serialPort', port.devicePath) }}><span className="small">Select</span></Button>}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Advanced */}
                <label className="small fw-semibold text-muted mb-2 d-block">Advanced Settings</label>
                <Row className="mb-3">
                    <Col md={3}><Form.Group><Form.Label className="small text-muted mb-1">Data Bits</Form.Label>
                        <Form.Select size="sm" value={config.dataBits} onChange={(e) => onChange('dataBits', parseInt(e.target.value))}>
                            {DATA_BITS_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}</Form.Select></Form.Group></Col>
                    <Col md={3}><Form.Group><Form.Label className="small text-muted mb-1">Stop Bits</Form.Label>
                        <Form.Select size="sm" value={config.stopBits} onChange={(e) => onChange('stopBits', parseInt(e.target.value))}>
                            {STOP_BITS_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}</Form.Select></Form.Group></Col>
                    <Col md={3}><Form.Group><Form.Label className="small text-muted mb-1">Parity</Form.Label>
                        <Form.Select size="sm" value={config.parity} onChange={(e) => onChange('parity', e.target.value)}>
                            {PARITY_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}</Form.Select></Form.Group></Col>
                    <Col md={3}><Form.Group><Form.Label className="small text-muted mb-1">Flow Control</Form.Label>
                        <Form.Select size="sm" value={config.flowControl} onChange={(e) => onChange('flowControl', e.target.value)}>
                            {FLOW_CONTROL_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}</Form.Select></Form.Group></Col>
                </Row>
                <Row className="mb-3">
                    <Col md={4}><Form.Group><Form.Label className="small text-muted mb-1">Buffer Flush Size</Form.Label>
                        <Form.Control size="sm" type="number" min={10} max={1024} value={config.bufferFlushSize}
                            onChange={(e) => onChange('bufferFlushSize', parseInt(e.target.value) || 50)} />
                        <Form.Text className="text-muted small">For continuous streams without newlines (e.g. <code>12345=12345=</code>): characters buffered before parsing. Default: 50.</Form.Text></Form.Group></Col>
                    <Col md={5}><Form.Group><Form.Label className="small text-muted mb-1">Weight Stabilization Tolerance (kg)</Form.Label>
                        <Form.Control size="sm" type="number" min={1} max={1000} value={config.stabilizationToleranceKg}
                            onChange={(e) => onChange('stabilizationToleranceKg', parseFloat(e.target.value) || 25)} />
                        <Form.Text className="text-muted small">On Capture, the weight locks in once consecutive readings stay within this many kg. Smaller = more precise but slower; larger = faster but less strict. Default: 25 kg.</Form.Text></Form.Group></Col>
                </Row>

                {/* Parser */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="small fw-semibold text-muted mb-0"><i className="bi bi-code-slash me-1 text-primary"></i>Data Parser Function</label>
                    <Button variant="outline-info" size="sm" onClick={() => setShowAIModal(true)}><i className="bi bi-magic me-1"></i>Code using AI</Button>
                </div>
                <Row className="mb-3">
                    <Col md={6}><Form.Group>
                        <Form.Label className="small fw-semibold text-muted mb-1">Weighbridge Type / Preset</Form.Label>
                        <Form.Select size="sm" value={config.weighbridgeType} onChange={(e) => onPresetChange(parseInt(e.target.value))}>
                            {PARSER_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </Form.Select>
                        {selectedPreset && <Form.Text className="text-muted small">{selectedPreset.description}</Form.Text>}
                    </Form.Group></Col>
                    <Col md={6}>
                        {selectedPreset && selectedPreset.sampleOutput && (
                            <Form.Group>
                                <Form.Label className="small fw-semibold text-muted mb-1">Sample Output</Form.Label>
                                <div className="px-3 py-2 rounded" style={{ backgroundColor: '#1e1e1e', fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: '13px' }}>
                                    <span className="text-success">{selectedPreset.sampleOutput}</span>
                                </div>
                            </Form.Group>
                        )}
                    </Col>
                </Row>
                <Form.Control as="textarea" value={config.parserFunction} onChange={(e) => onChange('parserFunction', e.target.value)} rows={12} spellCheck={false}
                    style={{ fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: '13px', lineHeight: '1.5', backgroundColor: '#1e1e1e', color: '#d4d4d4', border: '1px solid #333', borderRadius: '6px', padding: '12px 16px', resize: 'vertical', tabSize: 2 }} />
                {parserError ? (
                    <Alert variant="danger" className="mt-2 py-2 mb-0 small"><i className="bi bi-exclamation-triangle me-2"></i>Syntax Error: {parserError}</Alert>
                ) : config.parserFunction && (
                    <div className="mt-2 d-flex align-items-center">
                        <Badge bg="success" className="small"><i className="bi bi-check-circle me-1"></i>Valid</Badge>
                        <span className="text-muted small ms-2">Function compiles successfully</span>
                    </div>
                )}

                {/* Test connection */}
                <div className="d-flex align-items-center justify-content-between mt-3 pt-3 border-top">
                    <div>
                        <p className="small mb-1">Open a live serial monitor to verify data and test the parser.</p>
                        <p className="text-muted small mb-0">Opening the monitor saves this group first.</p>
                    </div>
                    <Button variant="outline-success" size="sm" onClick={handleOpenMonitor} disabled={!config.serialPort || !!parserError}>
                        <i className="bi bi-play-fill me-1"></i>Read Serial Data
                    </Button>
                </div>
            </Card.Body>

            <SerialMonitorModal show={showSerialModal} serialUrl={serialUrl} parserFunction={config.parserFunction}
                onGenerate={runAIGeneration} generating={aiGenerating} onHide={() => setShowSerialModal(false)} />
            <AIParserModal show={showAIModal} onHide={() => setShowAIModal(false)} onGenerate={runAIGeneration} generating={aiGenerating} brandName={config.brandName} />
        </Card>
    )
}

export default WeighbridgeConfigSection
